:q
<script setup lang="ts">
import type { UnwrapRef} from 'vue';
import {
  reactive, ref, watch, onMounted,
} from 'vue';
import { object, func } from 'vue-types';
import {
  Button,
  Input,
  Form,
  FormItem,
  Spin,
} from 'ant-design-vue';
import type { RuleObject, Rule } from 'ant-design-vue/lib/form';

import MfaCodeTrouble from './MfaCodeTrouble.vue';
import type { MfaFormValues} from './MfaTypes';
import { MfaInputIdEnum } from './MfaTypes';
import { onPaste, onKeyUp } from './MfaFunctions';

import type { ServerErrors } from '@/util/api/ajax/ApiResponse';
import ApiErrors from '@/ui/ApiErrors/index.vue';

const props = defineProps({
  initialValues: object<Partial<MfaFormValues>>().def(undefined),
  onFinish: func<(values: MfaFormValues) => void>().isRequired.def(undefined),
  error: object<ServerErrors>().def(undefined),
  loading: Boolean,
});

const formRef = ref();
const formValues: UnwrapRef<Partial<MfaFormValues>> = reactive({
  ...props.initialValues,
});
const formValuesJoined = ref('');
const focusRef = ref();
const resetFocus = () => {
  focusRef.value.focus();
};

onMounted(() => {
  resetFocus();
});

watch(formValues, (r) => {
  const digit1 = r.digit1 ?? '';
  const digit2 = r.digit2 ?? '';
  const digit3 = r.digit3 ?? '';
  const digit4 = r.digit4 ?? '';
  const digit5 = r.digit5 ?? '';
  const digit6 = r.digit6 ?? '';
  formValuesJoined.value = `${digit1}${digit2}${digit3}${digit4}${digit5}${digit6}`;
  formValues.code = Number(formValuesJoined.value);
});

const {
  digit1, digit2, digit3, digit4, digit5, digit6,
} = MfaInputIdEnum;

const validationMessage = 'Numbers Only';
const isNumberRule = async (_rule: Rule, value: string) => {
  if (value === '') {
    return Promise.reject(validationMessage);
  }

  if (Number.isNaN(Number(value))) {
    return Promise.reject(validationMessage);
  }

  return Promise.resolve();
};

const rules: Record<string, RuleObject | RuleObject[]> = {
  digit1: [
    {
      required: true,
      validator: isNumberRule,
      trigger: 'change',
      message: validationMessage,
    },
  ],
  digit2: [
    {
      required: true,
      validator: isNumberRule,
      trigger: 'change',
      message: validationMessage,
    },
  ],
  digit3: [
    {
      required: true,
      validator: isNumberRule,
      trigger: 'change',
      message: validationMessage,
    },
  ],
  digit4: [
    {
      required: true,
      validator: isNumberRule,
      trigger: 'change',
      message: validationMessage,
    },
  ],
  digit5: [
    {
      required: true,
      validator: isNumberRule,
      trigger: 'change',
      message: validationMessage,
    },
  ],
  digit6: [
    {
      required: true,
      validator: isNumberRule,
      trigger: 'change',
      message: validationMessage,
    },
  ],
};

const resetFormFields = () => {
  formRef.value.resetFields();
};

const isButtonDisabled = () => {
  if (props.loading) {
    return props.loading;
  }
  return formValuesJoined.value.length !== 6;
};

const onSubmit = () => {

  console.log('form submitted');
};

defineExpose({
  MfaInputIdEnum,
  resetFocus,
  resetFormFields,
});

</script>

<template>
  <h2 class="text-2xl text-center">
    One more thing...
  </h2>
  <p class="text-center w-75">
    Please enter the 6-digit code<br> we sent to your mobile device.
  </p>
  <Form
    ref="formRef"
    :model="formValues"
    :rules="rules"
    class="mfa-form"
    @finish="onFinish"
  >
    <Spin :spinning="!!loading">
      <div class="flex">
        <FormItem name="digit1" class="mr-3 inline-block">
          <Input
            :id="digit1"
            ref="focusRef"
            v-model:value="formValues.digit1"
            class="w-12 h-12 p-0 text-center inline mr-1"
            :maxlength="1"
            title="authentication code digit 1"
            required
            @keyup="onKeyUp"
            @paste="(evt: ClipboardEvent) => onPaste(evt, formValues)"
          />
        </FormItem>
        <FormItem name="digit2" class="mr-3 inline-block">
          <Input
            :id="digit2"
            v-model:value="formValues.digit2"
            class="w-12 h-12 p-0 text-center mr-1"
            :maxlength="1"
            title="authentication code digit 2"
            required
            @keyup="onKeyUp"
          />
        </FormItem>
        <FormItem name="digit3" class="mr-3 inline-block">
          <Input
            :id="digit3"
            v-model:value="formValues.digit3"
            class="w-12 h-12 p-0 text-center mr-1"
            :maxlength="1"
            title="authentication code digit 3"
            required
            @keyup="onKeyUp"
          />
        </FormItem>
        <FormItem name="digit4" class="mr-3 inline-block">
          <Input
            :id="digit4"
            v-model:value="formValues.digit4"
            class="w-12 h-12 p-0 text-center mr-1"
            :maxlength="1"
            title="authentication code digit 4"
            required
            @keyup="onKeyUp"
          />
        </FormItem>
        <FormItem name="digit5" class="mr-3 inline-block">
          <Input
            :id="digit5"
            v-model:value="formValues.digit5"
            class="w-12 h-12 p-0 text-center mr-1"
            :maxlength="1"
            title="authentication code digit 5"
            required
            @keyup="onKeyUp"
          />
        </FormItem>
        <FormItem name="digit6" class="mr-3 inline-block">
          <Input
            :id="digit6"
            v-model:value="formValues.digit6"
            class="w-12 h-12 p-0 text-center mr-1"
            :maxlength="1"
            title="authentication code digit 6"
            required
            @keyup="onKeyUp"
          />
        </FormItem>
      </div>
      <FormItem name="code" hidden>
        <Input id="code" v-model:value="formValuesJoined" />
      </FormItem>
    </Spin>
    <FormItem style="margin-bottom: 0">
      <Button
        :loading="loading"
        type="primary"
        html-type="submit"
        size="large"
        :class="{ 'w-full': true, hidden: !loading }"
        :disabled="isButtonDisabled()"
        @click="onSubmit"
      >
        Logging you in...
      </Button>
      <ApiErrors v-if="error" :error="error" class="mb-5 mt-5" />
      <MfaCodeTrouble />
    </FormItem>
  </Form>
</template>

<style scoped>
  .light .mfa-form .ant-form-item,
  .dark .mfa-form .ant-form-item {
    margin-right: 0.75rem;
  }

  .dark .mfa-form input {
    background-color: white;
    color: black;
  }

  .mfa-form input {
    background-color: white;
  }

  .mfa-form .hidden {
    display: none !important;
  }

  .dark .mfa-form .ant-form-item-has-error
  :not(.ant-input-disabled):not(.ant-input-borderless).ant-input {
    background: white;
  }

  .light .mfa-form .ant-form-item-explain-error,
  .dark .mfa-form .ant-form-item-explain-error {
    font-size: 0.9em!important;
  }

</style>
